<template>
  <a
    :href="path"
    class="
      tw-flex
      tw-flex-col
      tw-gap-4
      tw-bg-white
      tw-rounded-lg
      tw-p-6
      tw-shadow-sm
      tw-border
      tw-border-solid
      tw-border-transparent
      tw-text-sf-neutral-dark-1
      hover:tw-no-underline hover:tw-shadow-md hover:tw-border-sf-primary
      tw-transition-shadow
    "
    data-testid="pinned-report-card"
  >
    <header class="tw-flex tw-items-center tw-justify-between tw-gap-3">
      <h3
        class="
          tw-text-base
          tw-font-medium
          tw-text-sf-neutral-dark-1
          tw-m-0
          tw-truncate
        "
      >
        {{ name }}
      </h3>
    </header>

    <div
      v-if="pinned"
      class="tw-flex-1"
      :style="{ minHeight: PINNED_CHART_HEIGHT_DEFAULT }"
    >
      <ChartLoadState
        :graph-data="graphData"
        :loading="loading"
        :errored="errored"
        :error-text="I18n.t('report.drawer.pinned.load_error')"
        hide-x-axis-labels
        compact-y-axis
      />
    </div>

    <div
      v-if="highlighted.length > 0"
      class="tw-grid tw-gap-2"
      :class="gridColsClass"
      data-testid="pinned-report-card-highlighted"
    >
      <SfMetricPill
        v-for="entry in highlighted"
        :key="entry.id"
        variant="slim"
        :graph="graphFor(entry)"
        :value="entry.summary.valueDisplay ?? '—'"
        :delta="formatDelta(entry.summary)"
        :expanded="false"
        read-only
      />
    </div>
  </a>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'

import ChartLoadState from '@app/application/report/components/chart-load-state.vue'
import SfMetricPill from '@app/shared/vue/components/sf-metric-pill/sf-metric-pill.vue'
import { usePinnedGraphChart } from '@app/application/report/composables/use-pinned-graph-chart'
import { formatDelta } from '@app/application/report/components/graphs-drawer/format-summary'
import { PINNED_CHART_HEIGHT_DEFAULT } from '@app/application/report/constants'
import type { DrawerGraphEntry } from '@app/application/report/types'
import type { ReportGraph } from '@app/application/report/screens/graphs-echarts/types'
import type { Locale } from '@app/shared/types/locale'
import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({ name: 'PinnedReportCard' })

interface Props {
  reportTemplateId: number
  name: string
  locale: Locale
  path: string
  pinned: DrawerGraphEntry | null
  highlighted: DrawerGraphEntry[]
}

const props = defineProps<Props>()

const I18n = useI18n()

const reportTemplateIdRef = toRef(props, 'reportTemplateId')
const localeRef = toRef(props, 'locale')
const pinnedIdRef = computed(() => props.pinned?.id ?? null)

const gridColsClass = computed(() => {
  switch (props.highlighted.length) {
    case 1:
      return 'tw-grid-cols-1'
    case 2:
      return 'tw-grid-cols-2'
    default:
      return 'tw-grid-cols-3'
  }
})

const { graphData, loading, errored } = usePinnedGraphChart(
  pinnedIdRef,
  reportTemplateIdRef,
  localeRef
)

function graphFor(entry: DrawerGraphEntry): ReportGraph {
  return {
    id: entry.id,
    reportTemplateId: props.reportTemplateId,
    title: entry.summary.title,
    canEditGraph: false,
    locale: props.locale,
  }
}
</script>
